import { z } from 'zod';

import { emailSchema, newLoginSchema } from './identity';

/**
 * Contrato da members-api (`/api/members/...`): niveis, perfis cifrados,
 * pedidos de membro, quotas, encarregados e redes sociais.
 */

// ---------------------------------------------------------------------------
// Niveis (RN-A4)
// ---------------------------------------------------------------------------

/** `associate` nao e guardado: e um `member` com quota valida hoje. */
export const memberLevelSchema = z.enum(['user', 'member', 'associate']);
export type MemberLevel = z.infer<typeof memberLevelSchema>;

export const ADULT_AGE = 18;

// ---------------------------------------------------------------------------
// Cartao de Cidadao
// ---------------------------------------------------------------------------

/** Remove espacos e hifens e passa a maiusculas: `12345678 9 ZZ1` -> `123456789ZZ1`. */
export function normalizeCcNumber(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Valida o n.o de documento do Cartao de Cidadao (8 digitos do n.o civil, 1
 * digito de controlo, 2 caracteres de versao, 1 digito de controlo), pelo
 * algoritmo publicado pelo Estado (Luhn sobre base 36).
 */
export function isValidCcNumber(value: string): boolean {
  const cc = normalizeCcNumber(value);
  if (!/^\d{9}[A-Z0-9]{2}\d$/.test(cc)) return false;
  let sum = 0;
  let double = false;
  for (let i = cc.length - 1; i >= 0; i -= 1) {
    let n = Number.parseInt(cc.charAt(i), 36);
    if (double) {
      n *= 2;
      if (n >= 10) n -= 9;
    }
    sum += n;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * N.o civil: os 8 primeiros digitos do n.o do CC (ou o n.o do antigo BI). Nao
 * muda quando o cartao e renovado, por isso e por ele que se procuram
 * duplicados. `null` se o valor nao comecar por 8 digitos.
 */
export function civilNumberOf(value: string): string | null {
  const match = /^(\d{8})(?:\d|\d[A-Z0-9]{2}\d)?$/.exec(normalizeCcNumber(value));
  return match?.[1] ?? null;
}

export const ccNumberSchema = z
  .string()
  .trim()
  .transform(normalizeCcNumber)
  .refine(isValidCcNumber, 'numero do Cartao de Cidadao invalido');

// ---------------------------------------------------------------------------
// Perfil (RF-A4). Tudo isto e cifrado na base.
// ---------------------------------------------------------------------------

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'data no formato AAAA-MM-DD')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(v);
  }, 'data invalida');

export const birthDateSchema = isoDate.refine(
  (v) => v >= '1900-01-01' && v <= new Date().toISOString().slice(0, 10),
  'data de nascimento fora do intervalo',
);

export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^\+?\d{9,15}$/, 'telemovel invalido'));

export const addressSchema = z
  .object({
    street: z.string().trim().min(1).max(200),
    postalCode: z.string().trim().min(1).max(20),
    locality: z.string().trim().min(1).max(100),
    /** ISO 3166-1 alfa-2. */
    country: z.string().trim().toUpperCase().length(2).default('PT'),
  })
  .superRefine((a, ctx) => {
    if (a.country === 'PT' && !/^\d{4}-\d{3}$/.test(a.postalCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['postalCode'],
        message: 'codigo postal no formato 0000-000',
      });
    }
  });
export type Address = z.infer<typeof addressSchema>;

export const memberProfileSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  birthDate: birthDateSchema,
  contactEmail: emailSchema,
  phone: phoneSchema,
  ccNumber: ccNumberSchema,
  address: addressSchema,
});
export type MemberProfile = z.infer<typeof memberProfileSchema>;

/**
 * Perfil de um jovem registado na Arena: um menor pode nao ter email nem
 * telemovel proprios, e os contactos passam a ser os do encarregado.
 */
export const youthProfileSchema = memberProfileSchema.extend({
  contactEmail: emailSchema.nullable().default(null),
  phone: phoneSchema.nullable().default(null),
});
export type YouthProfile = z.infer<typeof youthProfileSchema>;

/**
 * Perfil importado da base antiga com dados em falta ou por corrigir. Cada
 * campo e o que veio, ja limpo, ou `null`; serve para preencher o formulario
 * de membro. Nunca e validado como perfil completo.
 */
export const importedDraftSchema = z.object({
  fullName: z.string().nullable(),
  birthDate: z.string().nullable(),
  /** O ano veio com 2 digitos e foi recuperado: a pessoa confirma. */
  birthDateUnconfirmed: z.boolean(),
  contactEmail: z.string().nullable(),
  phone: z.string().nullable(),
  /** Como estava na base antiga (pode ser so o n.o civil, ou o n.o do BI). */
  ccNumber: z.string().nullable(),
  address: z.object({
    street: z.string().nullable(),
    postalCode: z.string().nullable(),
    locality: z.string().nullable(),
    country: z.string().nullable(),
  }),
});
export type ImportedDraft = z.infer<typeof importedDraftSchema>;

/**
 * Codigos do que falta num perfil importado (o "porque" do membro inativo).
 * Aparecem no relatorio da importacao e em `GET /me`.
 */
export const importReasonSchema = z.enum([
  'sem_nome',
  'sem_data_nascimento',
  'data_nascimento_a_confirmar',
  'menor',
  'sem_email',
  'email_invalido',
  'email_repetido',
  'sem_telemovel',
  'cc_incompleto',
  'cc_invalido',
  'sem_morada',
  'codigo_postal_invalido',
  'sem_localidade',
  'pais_desconhecido',
  'conta_desativada',
]);
export type ImportReason = z.infer<typeof importReasonSchema>;

/**
 * O que falta, em frase, para o email de boas-vindas e o back-office. `menor` e
 * `conta_desativada` nao sao coisas que a pessoa complete: nao vao no email.
 */
export const IMPORT_REASON_TEXT: Record<ImportReason, string> = {
  sem_nome: 'o nome completo',
  sem_data_nascimento: 'a data de nascimento',
  data_nascimento_a_confirmar: 'confirmar a data de nascimento',
  menor: 'a autorizacao do encarregado de educacao (menor)',
  sem_email: 'um email',
  email_invalido: 'um email valido',
  email_repetido: 'um email so seu (o antigo era partilhado com outra conta)',
  sem_telemovel: 'o telemovel',
  cc_incompleto: 'o numero completo do Cartao de Cidadao',
  cc_invalido: 'um numero de Cartao de Cidadao valido',
  sem_morada: 'a morada',
  codigo_postal_invalido: 'um codigo postal valido',
  sem_localidade: 'a localidade',
  pais_desconhecido: 'o pais',
  conta_desativada: 'conta desativada no sistema antigo',
};

/** As frases do que falta, sem os motivos que nao sao para a pessoa completar. */
export function missingForEmail(reasons: readonly string[]): string[] {
  return reasons
    .filter((r): r is ImportReason => importReasonSchema.safeParse(r).success)
    .filter((r) => r !== 'menor' && r !== 'conta_desativada')
    .map((r) => IMPORT_REASON_TEXT[r]);
}

/** O que fica cifrado: a uniao dos dois (adulto ou jovem). */
export type StoredProfile = Omit<MemberProfile, 'contactEmail' | 'phone'> & {
  contactEmail: string | null;
  phone: string | null;
};

/** Idade em anos completos, a data `today` (`AAAA-MM-DD`). */
export function ageOn(birthDate: string, today: string): number {
  const [by, bm, bd] = birthDate.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) age -= 1;
  return age;
}

export function isMinorOn(birthDate: string, today: string): boolean {
  return ageOn(birthDate, today) < ADULT_AGE;
}

// ---------------------------------------------------------------------------
// Area de conta (`/api/members/me`)
// ---------------------------------------------------------------------------

export const applicationStatusSchema = z.enum(['pending', 'approved', 'rejected', 'withdrawn']);

export const socialNetworkSchema = z.enum(['discord', 'twitch', 'instagram', 'x', 'youtube']);
export type SocialNetwork = z.infer<typeof socialNetworkSchema>;
export const VERIFIED_NETWORKS = ['discord', 'twitch'] as const satisfies readonly SocialNetwork[];

export const socialLinkSchema = z.object({
  network: socialNetworkSchema,
  handle: z.string(),
  verified: z.boolean(),
});
export type SocialLink = z.infer<typeof socialLinkSchema>;

/** Nome de utilizador sem o @ (o YouTube aceita tambem hifens no @nome do canal). */
export const socialHandleRequestSchema = z.object({
  handle: z
    .string()
    .trim()
    .transform((v) => v.replace(/^@/, ''))
    .pipe(z.string().regex(/^[A-Za-z0-9._-]{1,30}$/, 'nome de utilizador invalido')),
});

/** Discord e Twitch: que ligacoes verificadas (OAuth) estao configuradas neste ambiente. */
export const socialProvidersResponseSchema = z.object({
  discord: z.boolean(),
  twitch: z.boolean(),
});
export type SocialProvidersResponse = z.infer<typeof socialProvidersResponseSchema>;

/** `POST /me/social/:rede/connect`: para onde mandar o browser autorizar. */
export const socialConnectResponseSchema = z.object({ url: z.string().url() });

/** `POST /me/social/:rede/callback`: o que a rede devolveu no regresso ao site. */
export const socialCallbackRequestSchema = z.object({
  code: z.string().min(1).max(500),
  state: z.string().min(1).max(500),
});

export const feePeriodSchema = z.object({
  validFrom: z.string(),
  validUntil: z.string(),
});
export type FeePeriod = z.infer<typeof feePeriodSchema>;

/**
 * Onde esta a quota de uma conta (RN-A7, RN-A8): ate quando vale, quando
 * acabou a ultima, se e altura de renovar e o que cobre uma quota paga hoje.
 */
export const feeStatusSchema = z.object({
  /** Ultimo dia (inclusive) da quota em vigor, com as ja pagas a seguir. */
  associateUntil: z.string().nullable(),
  /** Sem quota em vigor: o ultimo dia da ultima que houve. */
  lastUntil: z.string().nullable(),
  /**
   * Mostrar o convite a renovar: a quota acaba a 31/12 deste ano e ja e
   * novembro ou dezembro, ou a pessoa ja foi associada e voltou a membro.
   */
  renewalSuggested: z.boolean(),
  /** O periodo de uma quota paga hoje. `null` se a conta nao pode comprar a quota (nao e membro). */
  nextPeriod: feePeriodSchema.nullable(),
});
export type FeeStatus = z.infer<typeof feeStatusSchema>;

export const guardedYouthSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  authorizationActive: z.boolean(),
  authorizedSince: z.string().datetime().nullable(),
  /** Recebe o resumo semanal por email (RF-D4). */
  weeklyEmail: z.boolean(),
  /** Equipas (docs/equipas, parte 3): pode competir e aparecer em publico (RN-EQ3). */
  teamsCompete: z.boolean(),
  /** A foto de perfil aparece nas paginas das equipas (RN-EQ13). */
  teamsPhoto: z.boolean(),
  /** O jovem tambem pode ser associado, com a quota paga pelo encarregado (RN-A9). */
  level: memberLevelSchema,
  fee: feeStatusSchema,
});
export type GuardedYouth = z.infer<typeof guardedYouthSchema>;

export const memberMeResponseSchema = z.object({
  accountId: z.string().uuid(),
  level: memberLevelSchema,
  memberSince: z.string().datetime().nullable(),
  /** Ultimo dia (inclusive) da quota em vigor, se houver. */
  associateUntil: z.string().nullable(),
  fee: feeStatusSchema,
  profile: youthProfileSchema.nullable(),
  application: z
    .object({
      id: z.string().uuid(),
      status: applicationStatusSchema,
      submittedAt: z.string().datetime(),
      decidedAt: z.string().datetime().nullable(),
    })
    .nullable(),
  social: z.array(socialLinkSchema),
  /** Jovens de quem esta conta e encarregado (RN-A11). */
  youths: z.array(guardedYouthSchema),
  /**
   * Encarregados com acesso a esta conta. Um jovem da Arena que fez 18 anos e
   * passou a ter conta do site ve-os aqui e pode retirar-lhes o acesso; ate la
   * mantem-se (decisao da Grow uP, 18-10-2026).
   */
  guardians: z
    .array(
      z.object({
        accountId: z.string().uuid(),
        login: z.string(),
        since: z.string().datetime(),
      }),
    )
    .default([]),
  /**
   * Conta importada da base antiga com o perfil incompleto: os dados que
   * vieram, para preencher o pedido de membro, e o que falta. Ao enviar o
   * pedido com o perfil completo a conta volta logo a membro, com a data de
   * entrada original. `null` em todas as outras contas.
   */
  pendingImport: z
    .object({
      draft: importedDraftSchema,
      reasons: z.array(z.string()),
    })
    .nullable(),
});
export type MemberMeResponse = z.infer<typeof memberMeResponseSchema>;

/** `POST /api/members/me/application`: pedido de membro com os dados obrigatorios. */
export const memberApplicationRequestSchema = memberProfileSchema;

// ---------------------------------------------------------------------------
// Back-office
// ---------------------------------------------------------------------------

/**
 * Um membro visto por staff. Com `redacted: true` o perfil nao vem: quem pede
 * nao tem permissao para o ver (por exemplo, um monitor a olhar para um menor,
 * RN-A16). Fica so o login.
 */
export const memberViewSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  level: memberLevelSchema,
  origin: z.enum(['site', 'arena', 'import']),
  memberSince: z.string().datetime().nullable(),
  associateUntil: z.string().nullable(),
  isMinor: z.boolean().nullable(),
  redacted: z.boolean(),
  profile: youthProfileSchema.nullable(),
});
export type MemberView = z.infer<typeof memberViewSchema>;

export const applicationViewSchema = z.object({
  id: z.string().uuid(),
  status: applicationStatusSchema,
  submittedAt: z.string().datetime(),
  decidedAt: z.string().datetime().nullable(),
  decisionNote: z.string().nullable(),
  member: memberViewSchema,
});
export type ApplicationView = z.infer<typeof applicationViewSchema>;

export const applicationDecisionRequestSchema = z.object({
  note: z.string().trim().max(500).optional(),
});

/**
 * Como a quota foi paga (F-001): anual, anual com camisola ou mensal. A quota
 * e sempre do ano civil; um pagamento mensal (o primeiro incluido) torna
 * associado ate 31/12 desse ano, sem encadear nem regra de novembro.
 */
export const membershipFeePlanSchema = z.enum(['annual', 'annual_jersey', 'monthly']);
export type MembershipFeePlan = z.infer<typeof membershipFeePlanSchema>;

export const manualFeeRequestSchema = z.object({
  paidOn: isoDate,
  amountCents: z.number().int().nonnegative().default(3000),
  plan: membershipFeePlanSchema.default('annual'),
});

export const memberOriginSchema = z.enum(['site', 'arena', 'import']);
export type MemberOrigin = z.infer<typeof memberOriginSchema>;

/**
 * Pessoas com menos do que isto num sitio nao aparecem nesse sitio do mapa:
 * juntam-se ao distrito ou ficam so no total. Assim ninguem, sobretudo um
 * menor numa aldeia pequena, e identificavel pelo mapa (RF-A3).
 */
export const MEMBER_MAP_MIN_COUNT = 3;

/** `GET /api/members/map`: filtros do mapa de membros. */
export const memberMapQuerySchema = z.object({
  /** `member` = membros sem quota valida; `all` = membros e associados. */
  level: z.enum(['all', 'member', 'associate']).default('all'),
  origin: memberOriginSchema.optional(),
  /** Importados inativos (perfil incompleto). So contam com `level=all`. */
  inactive: z.enum(['include', 'exclude']).default('include'),
});
export type MemberMapQuery = z.infer<typeof memberMapQuerySchema>;

export const memberMapAreaSchema = z.object({
  key: z.string(),
  /**
   * `cp4`: um ou mais codigos postais de 4 digitos no mesmo sitio;
   * `district`: as zonas pequenas de um distrito juntas na sede;
   * `country`: um pais estrangeiro.
   */
  kind: z.enum(['cp4', 'district', 'country']),
  label: z.string(),
  /** Distrito (ou regiao autonoma) em Portugal; nome do pais fora. */
  region: z.string(),
  cp4: z.array(z.string()),
  lat: z.number(),
  lon: z.number(),
  count: z.number().int().positive(),
});
export type MemberMapArea = z.infer<typeof memberMapAreaSchema>;

/** Uma linha de total; `count` `null` = menos de `minCount`. */
const memberMapTotalSchema = z.object({
  name: z.string(),
  count: z.number().int().positive().nullable(),
});

export const memberMapResponseSchema = z.object({
  minCount: z.number().int().positive(),
  /** Todas as pessoas que os filtros apanham: `areas` + `hidden` + `unmapped`. */
  total: z.number().int().nonnegative(),
  /** Sitios do mapa, do maior para o menor. Contagens, nunca pessoas. */
  areas: z.array(memberMapAreaSchema),
  /** Em sitios com menos de `minCount` pessoas, mesmo juntando ao distrito. */
  hidden: z.number().int().nonnegative(),
  /** Sem morada, codigo postal desconhecido ou pais fora da lista. */
  unmapped: z.number().int().nonnegative(),
  districts: z.array(memberMapTotalSchema),
  countries: z.array(memberMapTotalSchema),
});
export type MemberMapResponse = z.infer<typeof memberMapResponseSchema>;

/** `GET /api/members/members`: filtros e pagina da lista do back-office. */
export const memberListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  level: memberLevelSchema.optional(),
  origin: memberOriginSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  /** `created` (a ordem de sempre) ou `seniority` (membro ha mais tempo primeiro). */
  sort: z.enum(['created', 'seniority']).default('created'),
});

/**
 * `GET /api/members/members/:id/details`: a ficha completa no back-office.
 * Encarregados e jovens aparecem so pelo login: os dados pessoais de cada um
 * seguem a regra de `member` (RN-A16).
 */
export const memberDetailsSchema = z.object({
  member: memberViewSchema,
  fees: z.array(
    z.object({
      validFrom: z.string(),
      validUntil: z.string(),
      amountCents: z.number().int(),
      source: z.enum(['store', 'manual', 'import']),
      plan: membershipFeePlanSchema,
      recordedAt: z.string().datetime(),
    }),
  ),
  /** Pagamentos mensais feitos este ano (F-001). Cobrar os que faltam e manual. */
  monthlyPaymentsThisYear: z.number().int(),
  applications: z.array(
    z.object({
      id: z.string().uuid(),
      status: applicationStatusSchema,
      submittedAt: z.string().datetime(),
      decidedAt: z.string().datetime().nullable(),
      decisionNote: z.string().nullable(),
    }),
  ),
  /** Encarregados deste jovem, com a autorizacao da Arena de cada um. */
  guardians: z.array(
    z.object({
      accountId: z.string().uuid(),
      login: z.string(),
      linkedAt: z.string().datetime(),
      authorizationActive: z.boolean(),
      authorizedSince: z.string().datetime().nullable(),
    }),
  ),
  /** Jovens de quem esta conta e encarregado. */
  youths: z.array(guardedYouthSchema),
});
export type MemberDetails = z.infer<typeof memberDetailsSchema>;

/**
 * `PUT /api/members/members/:id/level`: tornar membro sem pedido (importados,
 * inscricoes ao balcao) ou voltar a utilizador. Associado nao se escolhe aqui:
 * vem da quota.
 */
export const setLevelRequestSchema = z.object({
  level: z.enum(['user', 'member']),
});

/**
 * `PUT /members/:id/since` (`members.manage`): corrigir a data de entrada como
 * membro, que da os anos e a faixa de antiguidade da carta. Nos importados
 * vem quase sempre da criacao da conta no site antigo, nao da entrada real.
 */
export const setMemberSinceRequestSchema = z.object({
  since: isoDate.refine(
    (v) => v >= '1990-01-01' && v <= new Date().toISOString().slice(0, 10),
    'data entre 1990 e hoje',
  ),
});

// ---------------------------------------------------------------------------
// Registo de jovens na Arena (RF-A2, G.4 fluxo 2)
// ---------------------------------------------------------------------------

export const registerYouthRequestSchema = z
  .object({
    login: newLoginSchema,
    /** Arena onde o monitor esta a registar (a permissao e por arena). */
    arenaId: z.string().uuid(),
    profile: youthProfileSchema,
    /** Obrigatorio se o jovem for menor: recebe o convite para autorizar. */
    guardianEmail: emailSchema.nullable().default(null),
  })
  .superRefine((r, ctx) => {
    const today = new Date().toISOString().slice(0, 10);
    const minor = isMinorOn(r.profile.birthDate, today);
    if (minor && r.guardianEmail === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['guardianEmail'],
        message: 'menor: falta o email do encarregado de educacao',
      });
    }
    if (!minor && (r.profile.contactEmail === null || r.profile.phone === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['profile'],
        message: 'maior de idade: email e telemovel sao obrigatorios',
      });
    }
  });
export type RegisterYouthRequest = z.infer<typeof registerYouthRequestSchema>;

export const registerYouthResponseSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  isMinor: z.boolean(),
  /** `null` para maiores de idade. O email aparece mascarado. */
  guardianInvite: z
    .object({
      sentTo: z.string(),
      expiresAt: z.string().datetime(),
    })
    .nullable(),
});
export type RegisterYouthResponse = z.infer<typeof registerYouthResponseSchema>;

export const resendGuardianInviteRequestSchema = z.object({
  guardianEmail: emailSchema,
});

export const acceptGuardianInviteRequestSchema = z.object({
  token: z.string().min(1),
});

export const acceptGuardianInviteResponseSchema = z.object({
  youth: guardedYouthSchema,
});

// ---------------------------------------------------------------------------
// Interno (so na rede interna; header `X-Internal-Token`)
// ---------------------------------------------------------------------------

/**
 * `GET /internal/youths/:accountId/arena-eligibility`: o que a arena-api
 * pergunta ao desbloquear um PC (G.4 fluxo 4).
 */
export const arenaEligibilitySchema = z.object({
  accountId: z.string().uuid(),
  level: memberLevelSchema,
  isMinor: z.boolean(),
  /** `null` para maiores: nao precisam. */
  guardianAuthorized: z.boolean().nullable(),
  /** Tempo extra de associado (RF-A5, RN-B7). */
  isAssociate: z.boolean(),
  eligible: z.boolean(),
  reason: z.enum(['ok', 'not_member', 'no_profile', 'no_guardian_authorization']),
});
export type ArenaEligibility = z.infer<typeof arenaEligibilitySchema>;

/** `POST /internal/fees`: a site-api avisa que a quota foi paga (G.4 fluxo 3). */
export const internalFeePaidRequestSchema = z.object({
  accountId: z.string().uuid(),
  paidAt: z.string().datetime(),
  amountCents: z.number().int().nonnegative(),
  orderRef: z.string().min(1).max(100),
  plan: membershipFeePlanSchema.default('annual'),
});

/** `GET /internal/members/:accountId/level`: para a loja aplicar o desconto. */
export const internalLevelResponseSchema = z.object({
  accountId: z.string().uuid(),
  level: memberLevelSchema,
  associateUntil: z.string().nullable(),
});

/** `POST /internal/members/levels`: niveis de varias contas (roster dos eventos). */
export const internalLevelsRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
});
export const internalLevelsResponseSchema = z.object({
  levels: z.array(z.object({ accountId: z.string().uuid(), level: memberLevelSchema })),
});

/**
 * `POST /internal/members/minors` (pedido igual ao dos niveis): das contas
 * dadas, as que sao hoje de menores. Sem perfil conta como menor. Para o Hall
 * of Fame do site, que so mostra o nickname de quem e menor.
 */
export const internalMinorsResponseSchema = z.object({
  minors: z.array(z.string().uuid()),
});

/**
 * `GET /internal/guardians/:accountId/youths`: os jovens de quem a conta e
 * encarregado, para a site-api inscrever um jovem num evento (RN-E7).
 */
export const internalGuardedYouthsResponseSchema = z.object({
  youths: z.array(guardedYouthSchema),
});

/** As duas autorizacoes do encarregado para as equipas (RN-EQ3, RN-EQ13). */
export const TEAM_CONSENT_KINDS = ['compete', 'photo'] as const;
export const teamConsentKindSchema = z.enum(TEAM_CONSENT_KINDS);
export type TeamConsentKind = z.infer<typeof teamConsentKindSchema>;

/**
 * `POST /internal/youths/team-consents`: das contas dadas, as que tem
 * encarregado (jovens), com as autorizacoes das equipas e os encarregados a
 * quem vao os emails da equipa. Basta um encarregado autorizar.
 */
export const internalTeamConsentsResponseSchema = z.object({
  youths: z.array(
    z.object({
      accountId: z.string().uuid(),
      compete: z.boolean(),
      photo: z.boolean(),
      guardianIds: z.array(z.string().uuid()),
    }),
  ),
});
/** `POST /internal/members/team-photos`: so as contas com foto que se pode mostrar. */
export const internalTeamPhotosResponseSchema = z.object({
  photos: z.array(z.object({ accountId: z.string().uuid(), version: z.string() })),
});

export type InternalTeamConsents = z.infer<typeof internalTeamConsentsResponseSchema>['youths'];

/**
 * `GET /internal/members/:accountId/twitch`: a ligacao verificada da conta a
 * Twitch, para o Stream Portal (candidaturas). 404 se nao houver. So ligacoes
 * verificadas (OAuth); um nome escrito a mao nao conta.
 */
export const internalTwitchLinkSchema = z.object({
  accountId: z.string().uuid(),
  twitchId: z.string(),
  login: z.string(),
});
export type InternalTwitchLink = z.infer<typeof internalTwitchLinkSchema>;

/** Redes que um streamer pode mostrar na pagina publica (o @ de cada uma). */
export const publicSocialNetworkSchema = z.enum(['youtube', 'instagram', 'x']);
export type PublicSocialNetwork = z.infer<typeof publicSocialNetworkSchema>;

/**
 * `GET /internal/members/:id/streamer-profile`: o que a pagina publica de
 * streamers pode usar desta conta, se o streamer autorizar (docs/streaming).
 * Um menor (ou uma conta sem perfil) nunca tem foto aqui.
 */
export const internalStreamerProfileSchema = z.object({
  hasPhoto: z.boolean(),
  /** Versao da foto, para o cache do site. */
  photoVersion: z.string().nullable(),
  social: z.object({
    youtube: z.string().nullable(),
    instagram: z.string().nullable(),
    x: z.string().nullable(),
  }),
});
export type InternalStreamerProfile = z.infer<typeof internalStreamerProfileSchema>;

// ---------------------------------------------------------------------------
// Associados: resumo do ano no back-office (ciclo de vida da quota)
// ---------------------------------------------------------------------------

/** `GET /api/members/associates/summary`: o retrato das quotas hoje. */
export const associatesSummarySchema = z.object({
  /** Hoje, em Lisboa. */
  today: z.string(),
  year: z.number().int(),
  /** Membros com quota em vigor hoje. */
  associates: z.number().int(),
  /** Destes, quantos ja pagaram o ano seguinte. */
  renewed: z.number().int(),
  /** Destes, quantos tem a quota a acabar a 31/12 deste ano. */
  endingThisYear: z.number().int(),
  /** Voltaram a membro: a ultima quota acabou a 31/12 do ano passado ou ja este ano. */
  lapsed: z.number().int(),
  /** Quotas pagas este ano (loja e registadas a mao), sem as importadas. */
  feesPaidThisYear: z.number().int(),
  feesPaidThisYearCents: z.number().int(),
});
export type AssociatesSummary = z.infer<typeof associatesSummarySchema>;

// ---------------------------------------------------------------------------
// Seguimento da importacao (docs/importacao 3.5 e 3.6)
// ---------------------------------------------------------------------------

/** Uma conta importada com o perfil por completar, na fila do back-office. */
export const pendingImportItemSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** `youth`: entrou menor e ainda nao tem conta do site. */
  kind: z.enum(['standard', 'youth']),
  reasons: z.array(z.string()),
  /** Menor hoje, ou sem data de nascimento (na duvida, e menor). */
  minor: z.boolean(),
  /** So para quem pode ler os dados desta pessoa. */
  fullName: z.string().nullable(),
  importedAt: z.string().datetime(),
  /** Ja escolheu password (entrou no site novo). */
  activated: z.boolean(),
  hasEmail: z.boolean(),
  welcomeSentAt: z.string().datetime().nullable(),
});
export type PendingImportItem = z.infer<typeof pendingImportItemSchema>;

export const pendingImportListSchema = z.object({
  items: z.array(pendingImportItemSchema),
  /** Total com o filtro, antes da paginacao. */
  total: z.number().int(),
  /** Quantos por motivo, dos que este viewer ve (sem o filtro de motivo). */
  byReason: z.record(z.string(), z.number().int()),
});
export type PendingImportList = z.infer<typeof pendingImportListSchema>;

export const pendingImportQuerySchema = z.object({
  reason: importReasonSchema.optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

/** A ficha em espera: o que veio da base antiga, para completar. */
export const pendingImportDetailSchema = pendingImportItemSchema.extend({
  draft: importedDraftSchema,
});
export type PendingImportDetail = z.infer<typeof pendingImportDetailSchema>;

/**
 * `POST /api/members/imports/pending/:id/complete`: o staff completa a ficha.
 * Adulto: volta logo a membro, com a data de entrada original. Menor: fica
 * membro como um jovem registado na Arena, a espera do encarregado; com
 * `guardianEmail`, o convite segue logo.
 */
export const completeImportRequestSchema = youthProfileSchema.extend({
  guardianEmail: emailSchema.nullable().default(null),
});

/** `GET /api/members/imports/welcome/summary`: a campanha de boas-vindas. */
export const welcomeSummarySchema = z.object({
  /** Contas vindas da base antiga. */
  imported: z.number().int(),
  /** Ja escolheram password (entraram no site novo). */
  activated: z.number().int(),
  /** Receberam o email de boas-vindas. */
  sent: z.number().int(),
  /** Ainda por enviar: adultos com email, sem password e sem email enviado. */
  toSend: z.number().int(),
  /** Sem email de acesso (menores, email repetido ou invalido). */
  noEmail: z.number().int(),
  /** Contas que estavam desativadas no sistema antigo: so por reenvio manual. */
  disabled: z.number().int(),
});
export type WelcomeSummary = z.infer<typeof welcomeSummarySchema>;

/** Jovem (conta sem login no site) que ja fez 18 anos. */
export const turnedAdultItemSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  origin: z.enum(['site', 'arena', 'import']),
  fullName: z.string().nullable(),
  birthDate: z.string(),
  /** O email de contacto que ja havia, para sugerir. */
  contactEmail: z.string().nullable(),
  guardians: z.number().int(),
});
export type TurnedAdultItem = z.infer<typeof turnedAdultItemSchema>;

/** `POST /api/members/youths/:id/adult-account`: a conta passa a conta do site. */
export const adultAccountRequestSchema = z.object({
  email: emailSchema,
});
